import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ComparisonFAQ from '../components/ComparisonFAQ';
import ComparisonMatrix from '../components/ComparisonMatrix';
import ComparisonToolCard from '../components/ComparisonToolCard';
import { buyerFaqs, comparisonSchema, comparisonTools } from '../data/comparisonPageContent';

const BestAiReceptionistPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Best AI Receptionist for Small Business (2026)';
    const description = 'Compare the best AI receptionist software for small businesses in 2026, including AutoReception, Smith.ai, Dialpad, and Bland AI.';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = description;
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.textContent = JSON.stringify(comparisonSchema);
    document.head.appendChild(schema);
    return () => schema.remove();
  }, []);

  return (
    <div className="animate-in fade-in duration-700">
      <header className="px-4 pb-16 pt-36 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <p className="mb-5 text-sm font-bold uppercase tracking-widest text-blue-400">2026 independent comparison</p>
          <h1 className="text-5xl font-extrabold leading-tight tracking-tight text-white md:text-7xl">7 Best AI Receptionist Software for Small Businesses in 2026</h1>
          <p className="mx-auto mt-7 max-w-3xl text-lg leading-relaxed text-slate-300">Small businesses are replacing legacy call centers with voice AI because customers expect immediate answers, even after hours. We evaluated these tools on voice latency and naturalness, calendar integration, ease of setup, call routing, and pricing transparency. The right choice depends on whether you need a fast local-business deployment, human backup, unified communications, hospitality workflows, or a developer platform.</p>
        </div>
      </header>

      <main className="space-y-24 px-4 pb-24 sm:px-6 lg:px-8">
        <section className="mx-auto max-w-6xl">
          <div className="mb-10 text-center"><h2 className="text-4xl font-extrabold text-white">Editor&apos;s top picks at a glance</h2></div>
          <div className="overflow-x-auto rounded-3xl border border-slate-800">
            <table className="min-w-[760px] w-full text-left">
              <thead className="bg-slate-800 text-sm uppercase tracking-wider text-slate-300"><tr><th className="p-5">Category</th><th className="p-5">Recommended tool</th></tr></thead>
              <tbody className="divide-y divide-slate-800 bg-slate-900/80 text-slate-300">
                {[
                  ['Best Overall for SMBs', 'AutoReception'],
                  ['Best Hybrid (Human + AI)', 'Smith.ai'],
                  ['Best for Custom Developer Workflows', 'Bland AI / Vapi'],
                  ['Best for High-Volume Simple Booking', 'My AI Front Desk'],
                  ['Best for Multi-Location Franchises', 'Slang.ai']
                ].map(([category, tool]) => <tr key={category}><th className="p-5 font-semibold text-white">{category}</th><td className="p-5 text-blue-300">{tool}</td></tr>)}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mx-auto max-w-6xl space-y-6">
          <div className="max-w-3xl"><p className="mb-4 text-sm font-bold uppercase tracking-widest text-blue-400">Detailed reviews</p><h2 className="text-4xl font-extrabold text-white">Seven tools, seven different strengths.</h2><p className="mt-4 leading-relaxed text-slate-400">Pricing and feature availability change frequently. Treat the snapshots below as buying guidance, then confirm current terms, usage allowances, integrations, and transfer charges with each vendor.</p></div>
          {comparisonTools.map((tool) => <ComparisonToolCard key={tool.name} tool={tool} />)}
        </section>

        <section className="mx-auto max-w-6xl">
          <div className="mb-10"><p className="mb-4 text-sm font-bold uppercase tracking-widest text-blue-400">Side-by-side</p><h2 className="text-4xl font-extrabold text-white">Feature comparison matrix</h2></div>
          <ComparisonMatrix />
        </section>

        <section className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <div><p className="mb-4 text-sm font-bold uppercase tracking-widest text-blue-400">Buyer&apos;s guide</p><h2 className="text-4xl font-extrabold text-white">Choose based on how your business actually operates.</h2></div>
          <div className="space-y-5 text-slate-300">
            <p><strong className="text-white">Per-minute vs. flat monthly billing:</strong> Compare included minutes, transfers, phone numbers, SMS, setup charges, and overages. A low entry price can become expensive when call volume rises.</p>
            <p><strong className="text-white">Voice latency and interruptibility:</strong> Sub-second responses and natural interruption handling make conversations feel useful instead of scripted.</p>
            <p><strong className="text-white">Calendar and CRM integrations:</strong> Confirm the system can check real availability, book the right appointment type, and send lead details where your team already works.</p>
            <p><strong className="text-white">Porting vs. forwarding:</strong> Call forwarding is usually the fastest launch path. Porting may simplify long-term phone administration but can require more planning and carrier coordination.</p>
          </div>
        </section>

        <section className="mx-auto max-w-4xl">
          <div className="mb-10 text-center"><p className="mb-4 text-sm font-bold uppercase tracking-widest text-blue-400">Buyer FAQ</p><h2 className="text-4xl font-extrabold text-white">Common questions before choosing a platform</h2></div>
          <ComparisonFAQ />
        </section>

        <section className="mx-auto max-w-5xl rounded-3xl bg-blue-700 px-6 py-16 text-center shadow-2xl sm:px-12">
          <h2 className="text-4xl font-extrabold text-white">Want to test the best fit for your call volume?</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-blue-100">Try AutoReception risk-free or listen to the interactive live demo before changing your phone workflow.</p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/ai-receptionist-for-small-business" className="rounded-full bg-white px-7 py-4 font-bold text-blue-700 transition hover:bg-blue-50">Test AutoReception Risk-Free</Link>
            <Link to="/ai-receptionist-for-small-business#demo" className="rounded-full border border-blue-200/50 px-7 py-4 font-bold text-white transition hover:bg-blue-600">Listen to the Live Demo</Link>
          </div>
        </section>

        <section className="mx-auto max-w-4xl text-center text-slate-400">
          <p>See our <Link to="/ai-receptionist-for-small-business" className="text-blue-400 hover:underline">dedicated AI receptionist for small business</Link> page for product details, setup expectations, and ROI considerations.</p>
          <p className="mt-3">If you prefer human-assisted coverage, compare the <Link to="/virtual-receptionist-for-small-business" className="text-blue-400 hover:underline">virtual receptionist for small business</Link> approach.</p>
        </section>
      </main>
    </div>
  );
};

export default BestAiReceptionistPage;